import React, { useState, useRef } from "react";
import { Tooltip, theme, Modal, Button } from "antd";
import { MinusOutlined, PlusCircleFilled, EyeOutlined, EditOutlined, StopFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import ColorButton from "../../common/ColorButton";
import { formatComma } from "../../frameworks/Util";
import TSTable from "../../common/TSTable";
import { GRAY_COLOR, ORANGE_COLOR, PRIMARY_COLOR } from "../../constants/color";
import ErrorMessages from "../../common/ErrorMessages";
import AddStockSkimModal from "./AddStockSkimModal";
import StockSkimHistoryModal from "./StockSkimHistoryModal";
import { URL_SKIM } from "../../constants/urls";
import ManageStockSkimModal from "./ManageStockSkimModal";
import { DELETE, POST } from "../../frameworks/HttpClient";
import CustomDeleteConfirmButton from "../../common/CustomDeleteConfirmButton";

export default function StockSkimList() {
  const tableRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [openAddStockSkimModal, setOpenAddStockSkimModal] = React.useState(false);
  const [openStockSkimHistoryModal, setOpenStockSkimHistoryModal] = React.useState(false);
  const [openWithdawStockModal, setOpenWithdawStockModal] = React.useState(null);
  const [ errorMessages, setErrorMessages] = useState(null);
  const { token: { colorWarning, colorSuccess, colorError }, } = theme.useToken();

  const refreshData = async () => {
    if (tableRef.current) {
      setErrorMessages(null);
      try {
        await tableRef.current.fetchWithoutResetPage();
      } catch (error) { 
        setErrorMessages(error.errorMessages);
      }
    }
  };

  const handleEndWork = async(id) => {
    try {
      await POST(`${URL_SKIM.SKIM_STOCK}${id}/close/`)
      refreshData();
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const handleDelete = async(id) => {
    try {
      await DELETE(`${URL_SKIM.SKIM_STOCK}${id}/`)
      refreshData();
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }


  const confirmEnd = (title, value) => {
    Modal.confirm({
      title: `จบการทำงาน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'คุณต้องการจบการทำงานพาเลทใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleEndWork(value)},
      onConfirm: () => refreshData()
    });
  };

  const columns = [
    {
      title: "Action",
      dataIndex: "",
      width: 200,
      key: "action",
      align: "center",
      render: (_, record) =>
        (
          <div onClick={(e) => e.stopPropagation()}>

            {record.is_closed === false &&
            <Tooltip triger='hover' title="แก้ไข"> 
              <ColorButton
                type="primary"
                override={colorWarning}
                icon={<EditOutlined />}
                style={{ margin : 4 }}
                onClick={() => {
                  setOpenAddStockSkimModal(true);
                  setTarget(record);
                }}
              />
            </Tooltip>}

            {record.is_closed === false &&
            <Tooltip triger='hover' title="ตัดสต๊อก"> 
              <ColorButton
                type="primary"
                override={ORANGE_COLOR}
                icon={<MinusOutlined />}
                style={{ margin : 4 }}
                onClick={() => {
                  setOpenWithdawStockModal('withdraw')
                  setTarget(record)
                }}
              /> 
            </Tooltip>}

            {(record.is_closed === false && record.deliveried === false) &&
              <CustomDeleteConfirmButton
                title={`ลบพาเลทสกิม`}
                content={"คุณต้องการลบพาเลทสกิมใช่หรือไม่"}
                onOk={ async() => {
                  await handleDelete(record.id)
                  refreshData();
                }}
                onConfirm={() => refreshData()}
              />
            }

            {record.is_closed === false &&
            <Tooltip triger='hover' title={"จบการทำงาน"}>
              <ColorButton 
                type='primary'
                override={GRAY_COLOR}
                style={{ margin : 4 }}
                icon={<StopFilled />}
                onClick={()=> confirmEnd(record.stock_no, record.id)} 
            /></Tooltip>}
            
          </div>
        ),
    },
    {
      title: "เลขที่พาเลท",
      dataIndex: "stock_no",
      key: "stock_no",
      searcher : true,
    },
    {
      title: "ล็อตการผลิต / ล็อตใบแจ้งผลิต",
      dataIndex: "sources",
      key: "sources",
      render : (value, original)  => 
        <ui class='list-circle'>
          {original && original.sources.map((items) => 
            <li>{`${items.lot_no} / ${items.produce_lot_no ? items.produce_lot_no : 'ไม่มีใบแจ้งผลิต'}`}</li>
          )}
        </ui>
    },
    {
      title: "จำนวนทั้งหมด",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: (value) => formatComma(value)
    },
    {
      title: "จำนวนที่เหลือ",
      dataIndex: "current",
      key: "current",
      render: (value, record) => <center>
        <div><text style={{ marginBottom : 10 }}>{formatComma(value)}</text>
          <Tooltip triger='hover' title="ประวัติสต๊อกสกิม"> 
            <Button 
              color='primary' 
              variant='link'
              icon={<EyeOutlined/>}
              onClick={() => {
                setOpenStockSkimHistoryModal(true)
                setTarget(record);
            }}/>
          </Tooltip>
        </div>
      </center>
    },
    {
      title: "สินค้า",
      dataIndex: "product_name",
      key: "product_name",
      align: "center",
    },
    {
      title: "ชื่อย่อสินค้า",
      dataIndex: "product_short_name",
      key: "product_short_name",
      align: "center",
    }
  ];

  return (
    <div>
      <ErrorMessages message={errorMessages}/>
      <ColorButton
        override={colorSuccess}
        type="primary"
        style={{ marginBottom: 10, float: "right"   }}
        icon={<PlusCircleFilled />}
        onClick={() => {
          setOpenAddStockSkimModal(true)
        }}
      >
        เพิ่มพาเลท
      </ColorButton>

      <TSTable
        columns={columns}
        size="small"
        url={URL_SKIM.SKIM_STOCK}
        ref={tableRef}
      />

      <AddStockSkimModal
        open={openAddStockSkimModal}
        target={target}
        onClose={() => {
          setOpenAddStockSkimModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenAddStockSkimModal(false);
          setTarget(null);
          refreshData();
        }}
      />

      <StockSkimHistoryModal
        open={openStockSkimHistoryModal}
        target={target}
        onClose={() => {
          setOpenStockSkimHistoryModal(false);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenStockSkimHistoryModal(false);
          setTarget(null);
          refreshData();
        }}
      />

      <ManageStockSkimModal
        open={openWithdawStockModal ? true : false}
        target={target}
        direction={openWithdawStockModal}
        onClose={() => {
          setOpenWithdawStockModal(null);
          setTarget(null);
        }}
        onUpdate={() => {
          setOpenWithdawStockModal(false);
          setTarget(null);
          refreshData();
        }}
      />

    </div>
  );
}
